import { socketOn } from './useSocket';
import { registerStaleDataRefetchHandler } from './stale-data-dispatcher';
import { refetchKeys, type RefetchKey, type StaleDataEventPayload } from './socket.types';

const knownRefetchKeys = new Set<RefetchKey>(Object.values(refetchKeys));

const getPayloadRefetchKeys = (payload?: StaleDataEventPayload): unknown[] | undefined => {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  if ('refetchKeys' in payload && Array.isArray(payload.refetchKeys)) {
    return payload.refetchKeys;
  }

  if ('data' in payload && Array.isArray(payload.data?.refetchKeys)) {
    return payload.data.refetchKeys;
  }

  return undefined;
};

export const getRefetchKeysFromStaleData = (payload?: StaleDataEventPayload): RefetchKey[] => {
  const rawRefetchKeys = getPayloadRefetchKeys(payload);

  if (!rawRefetchKeys) {
    return [];
  }

  return rawRefetchKeys.reduce<RefetchKey[]>((keys, key) => {
    const normalizedKey = String(key).toUpperCase() as RefetchKey;

    if (!knownRefetchKeys.has(normalizedKey) || keys.includes(normalizedKey)) {
      return keys;
    }

    keys.push(normalizedKey);
    return keys;
  }, []);
};

export const hasRefetchKey = (payload: StaleDataEventPayload, key: RefetchKey): boolean => {
  return getRefetchKeysFromStaleData(payload).includes(key);
};

export const onStaleDataUpdate = (
  listener: (payload: StaleDataEventPayload, keys: RefetchKey[]) => void,
): (() => void) => {
  const unsubscribeStaleDataUpdate = socketOn('stale_data_update', (payload) => {
    listener(payload, getRefetchKeysFromStaleData(payload));
  });
  const unsubscribeClientDataUpdated = socketOn('client_data_updated', (payload) => {
    listener(payload, getRefetchKeysFromStaleData(payload));
  });

  return () => {
    unsubscribeStaleDataUpdate();
    unsubscribeClientDataUpdated();
  };
};

export const onStaleDataRefetchKey = (
  key: RefetchKey,
  listener: (payload: StaleDataEventPayload, keys: RefetchKey[]) => void,
): (() => void) => {
  return registerStaleDataRefetchHandler(key, listener);
};
